import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * ProtectedRoute Component
 * Validates user authentication and role before rendering protected content
 * 
 * Features:
 * - Shows loading state while role is being fetched from Firestore
 * - Validates required roles (single or array of roles)
 * - Checks session timeout (24 hours)
 * - Integrates with AuthContext for real-time role updates
 * 
 * Usage:
 * <ProtectedRoute requiredRole="teacher">
 *   <TeacherDashboard />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ children, requiredRole = null }) => {
    const { currentUser, userRole, loading } = useAuth();

    // Show loading state while authenticating and fetching role from Firestore
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: '#f5f5f5'
                }}
            >
                <CircularProgress size={50} />
                <Typography sx={{ mt: 2, color: 'textSecondary' }}>
                    Loading your profile...
                </Typography>
            </Box>
        );
    }

    // Not authenticated - redirect to login
    if (!currentUser || !userRole) {
        return <Navigate to="/login" replace />;
    }

    // Validate required role(s)
    if (requiredRole) {
        const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        const normalizedUserRole = userRole.toLowerCase().replace(/_/g, '-');
        const normalizedRoles = roles.map(r => r.toLowerCase().replace(/_/g, '-'));
        
        if (!normalizedRoles.includes(normalizedUserRole)) {
            console.warn(`User role "${userRole}" does not match required roles:`, roles);
            return <Navigate to="/error" replace />;
        }
    }

    // User is authenticated with valid role
    return children;
};

export default ProtectedRoute;
